import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, renderSlot as _renderSlot, vShow as _vShow, withDirectives as _withDirectives } from "vue"

import { computed, useSlots } from "vue";
import { GroupableTableRowProps, TableColumn } from "@/types";
import { camelcaseToDisplay } from "@/util";
import { artifactStore, useVModel } from "@/hooks";
import {
  Typography,
  AttributeChip,
  FlexBox,
} from "@/components/common/display";
import { IconButton } from "@/components/common/button";


/**
 * Renders a row of the groupable table.
 */
const __default__ = {
  name: "GroupableTableRow",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    quasarProps: {},
    row: {},
    columns: {},
    expand: { type: Boolean },
    expandable: { type: Boolean }
  },
  emits: ["click", "update:expanded", "group:open", "group:close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const slots = useSlots();

const rowExpanded = useVModel(props, "expand");

const groupBy = computed(() => String(props.row.$groupBy || ""));
const groupHeader = computed(() => `${camelcaseToDisplay(groupBy.value)}:`);
const groupValue = computed(() => String(props.row.$groupValue));
const groupRows = computed(() => String(props.row.$groupRows));

const groupIsArtifactType = computed(() =>
  ["sourceType", "targetType", "type"].includes(groupBy.value)
);
const groupIsScore = computed(() => groupBy.value === "score");
const groupDisplayArtifact = computed(() =>
  ["sourceName", "targetName"].includes(groupBy.value)
);
const groupHeaderType = computed(() =>
  groupDisplayArtifact.value
    ? artifactStore.getArtifactByName(groupValue.value)?.type
    : undefined
);
const groupHeaderDescription = computed(() =>
  groupDisplayArtifact.value
    ? artifactStore.getArtifactByName(groupValue.value)?.body
    : undefined
);

/**
 * Returns the display value for a column on this row.
 * @param column - The column to display.
 * @return The display value.
 */
function getColumnDisplayValue(column: TableColumn): string {
  return column.format
    ? column.format(column.field(props.row))
    : String(column.field(props.row));
}

return (_ctx: any,_cache: any) => {
  const _component_q_td = _resolveComponent("q-td")!
  const _component_q_tr = _resolveComponent("q-tr")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (groupBy.value)
      ? (_openBlock(), _createBlock(_component_q_tr, {
          key: 0,
          "data-cy": "artifact-table-group"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_td, { colspan: "100%" }, {
              default: _withCtx(() => [
                _createElementVNode("div", null, [
                  _createVNode(_unref(FlexBox), {
                    align: "center",
                    justify: "between"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(FlexBox), { align: "center" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Typography), {
                            value: groupHeader.value,
                            r: "2",
                            "data-cy": "artifact-table-group-type"
                          }, null, 8, ["value"]),
                          _createVNode(_unref(AttributeChip), {
                            value: groupValue.value,
                            "artifact-type": groupIsArtifactType.value,
                            "confidence-score": groupIsScore.value,
                            "data-cy": "artifact-table-group-value"
                          }, null, 8, ["value", "artifact-type", "confidence-score"]),
                          (groupDisplayArtifact.value && groupHeaderType.value)
                            ? (_openBlock(), _createBlock(_unref(AttributeChip), {
                                key: 0,
                                value: groupHeaderType.value,
                                "artifact-type": ""
                              }, null, 8, ["value"]))
                            : _createCommentVNode("", true),
                          _createVNode(_unref(Typography), {
                            secondary: "",
                            value: groupRows.value,
                            x: "2"
                          }, null, 8, ["value"])
                        ]),
                        _: 1
                      }),
                      (props.expandable)
                        ? (_openBlock(), _createBlock(_unref(FlexBox), { key: 0 }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(IconButton), {
                                small: "",
                                color: "negative",
                                tooltip: "Collapse All",
                                icon: "group-close-all",
                                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('group:close', groupBy.value, props.row.$groupValue)))
                              }),
                              _createVNode(_unref(IconButton), {
                                small: "",
                                color: "primary",
                                tooltip: "Expand All",
                                icon: "group-open-all",
                                onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('group:open', groupBy.value, props.row.$groupValue)))
                              })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }),
                  (!!groupHeaderDescription.value)
                    ? (_openBlock(), _createBlock(_unref(Typography), {
                        key: 0,
                        "default-expanded": "",
                        variant: "expandable",
                        value: groupHeaderDescription.value
                      }, null, 8, ["value"]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : (_openBlock(), _createBlock(_component_q_tr, {
          key: 1,
          props: props.quasarProps,
          class: "cursor-pointer",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('click')))
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.columns, (column, idx) => {
              return (_openBlock(), _createBlock(_component_q_td, {
                key: column.name
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(FlexBox), {
                    align: "center",
                    justify: idx === 0 ? 'start' : 'end'
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", {
                        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        (idx === 0 && props.expandable)
                          ? (_openBlock(), _createBlock(_unref(IconButton), {
                              key: 0,
                              tooltip: "Toggle expand",
                              icon: props.expand ? 'up' : 'down',
                              class: "q-mr-sm",
                              onClick: _cache[2] || (_cache[2] = ($event: any) => (rowExpanded.value = !_unref(rowExpanded)))
                            }, null, 8, ["icon"]))
                          : _createCommentVNode("", true)
                      ]),
                      (!!_unref(slots)[`body-cell-${column.name}`])
                        ? _renderSlot(_ctx.$slots, `body-cell-${column.name}`, {
                            key: 0,
                            row: props.row
                          })
                        : (_openBlock(), _createBlock(_unref(Typography), {
                            key: 1,
                            value: getColumnDisplayValue(column)
                          }, null, 8, ["value"]))
                    ]),
                    _: 2
                  }, 1032, ["justify"])
                ]),
                _: 2
              }, 1024))
            }), 128))
          ]),
          _: 3
        }, 8, ["props"])),
    (!groupBy.value)
      ? _withDirectives((_openBlock(), _createBlock(_component_q_tr, {
          key: 2,
          props: props.quasarProps
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_td, {
              colspan: "100%",
              class: "bg-background"
            }, {
              default: _withCtx(() => [
                _renderSlot(_ctx.$slots, "body-expanded", {
                  row: props.row
                })
              ]),
              _: 3
            })
          ]),
          _: 3
        }, 8, ["props"])), [
          [_vShow, props.expand]
        ])
      : _createCommentVNode("", true)
  ], 64))
}
}

})